import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Container,
  Stack,
  TextField,
  InputAdornment,
  Grid,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Pagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { useTheme } from "@emotion/react";
import { AddDialog } from "./AddDialog";
import { ModifyDialog } from "./ModifyDialog";
import { ReadDialog } from "./ReadDialog";
import { AvvisiProvider, useAvvisi } from "./AvvisiContext";

const parseDate = (dateStr) => {
  if (!dateStr) return new Date();
  // Expecting "DD/MM/YYYY HH:mm:ss"
  const [datePart, timePart] = dateStr.split(" ");
  if (!datePart) return new Date();
  const [day, month, year] = datePart.split("/");
  const [hour, minute, second] = timePart ? timePart.split(":") : [0, 0, 0];
  return new Date(year, month - 1, day, hour, minute, second);
};

const AvvisiContent = () => {
  const theme = useTheme();
  const { avvisiList, loading, addAvviso, deleteAvviso, fetchAvvisi } =
    useAvvisi();
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openReadModal, setOpenReadModal] = useState(false);
  const [selectedAvviso, setSelectedAvviso] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;
  const [openModal, setOpenModal] = useState(false);

  // Filter States
  const [filterType, setFilterType] = useState("all");

  const filteredAvvisi = avvisiList?.filter((a) => {
    // Search Text
    const matchesSearch =
      a.titolo && a.titolo.toLowerCase().includes(searchText.toLowerCase());

    // Filter Type (IdTipologia)
    const matchesType =
      filterType === "all" || a.idTipologia.toString() === filterType;

    return matchesSearch && matchesType;
  });

  const totalPages = Math.ceil((filteredAvvisi?.length || 0) / rowsPerPage);
  const visibleAvvisi = filteredAvvisi?.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [searchText, filterType]);

  const handleOpenReadDialog = (avviso) => {
    setSelectedAvviso(avviso);
    setOpenReadModal(true);
  };

  const getTypeConfig = (typeId) => {
    switch (typeId) {
      case 2: // Urgent?
        return {
          icon: <WarningIcon />,
          color: "#d32f2f",
          bg: "#fdecea",
          label: "Urgente",
        };
      case 3: // Task?
        return {
          icon: <AssignmentIcon />,
          color: "#ed6c02",
          bg: "#fff4e5",
          label: "Task",
        };
      case 1: // Info
      default:
        return {
          icon: <InfoIcon />,
          color: "#0288d1",
          bg: "#e1f5fe",
          label: "Info",
        };
    }
  };

  const handleOpenModal = (avviso) => {
    setSelectedAvviso(avviso);
    setOpenModal(true);
  };

  const handleSearch = () => {
    fetchAvvisi(searchText, filterType);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {/* Filter Bar */}
          <Paper
            sx={{ p: 2, mb: 3, borderRadius: 2 }}
            elevation={0}
            variant="outlined"
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Cerca tra gli avvisi..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={filterType}
                    label="Tipo"
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <MenuItem value="all">Tutti</MenuItem>
                    <MenuItem value="1">Info</MenuItem>
                    <MenuItem value="2">Urgente</MenuItem>
                    <MenuItem value="3">Task</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={1}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  startIcon={<InfoIcon />}
                  onClick={handleSearch}
                >
                  Cerca
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
                md={2}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  startIcon={<InfoIcon />}
                  onClick={() => setOpenAddModal(true)}
                >
                  Nuovo Avviso
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Notifications List SCROLL AREA */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              maxHeight: "100vh",
              overflowY: "auto",
              bgcolor: theme.palette.mode === "dark" ? "#1B2635" : "#F7F9FC",
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2}>
                {visibleAvvisi?.length === 0 ? (
                  <Box
                    sx={{ textAlign: "center", py: 8, color: "text.secondary" }}
                  >
                    <Typography variant="h6">Nessun avviso trovato</Typography>
                    <Typography variant="body2">
                      Non ci sono notifiche che corrispondono alla tua ricerca.
                    </Typography>
                  </Box>
                ) : (
                  visibleAvvisi?.map((avviso) => {
                    const typeConfig = getTypeConfig(avviso.idTipologia);
                    const creationDate = parseDate(avviso.dataCreazione);

                    return (
                      <Card
                        key={avviso.idUid}
                        elevation={2}
                        sx={{
                          borderRadius: 2,
                          borderLeft: `6px solid ${typeConfig.color}`,
                          transition: "transform 0.2s",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: 4,
                          },
                        }}
                      >
                        <CardContent sx={{ pb: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: "center",
                              }}
                            >
                              {/* Since we don't have sender info, use a generic icon or the type icon */}
                              <Avatar
                                sx={{
                                  width: 48,
                                  height: 48,
                                  bgcolor: typeConfig.bg,
                                  color: typeConfig.color,
                                }}
                              >
                                {typeConfig.icon}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="h5"
                                  sx={{
                                    lineHeight: 1.2,
                                    mt: 0.5,
                                    whiteSpace: "pre-line",
                                    cursor: "pointer",
                                    "&:hover": {
                                      textDecoration: "underline",
                                    },
                                    color: "#407ad6",
                                  }}
                                  onClick={() => handleOpenModal(avviso)}
                                >
                                  {avviso.titolo}
                                </Typography>
                                <Typography
                                  variant="subtitle3"
                                  sx={{ fontSize: "12px" }}
                                  color="text.secondary"
                                >
                                  {creationDate.toLocaleDateString()} •{" "}
                                  {creationDate.toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              label={typeConfig.label}
                              size="small"
                              sx={{
                                bgcolor: typeConfig.bg,
                                color: typeConfig.color,
                                fontWeight: "bold",
                              }}
                            />
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              gap: 3,
                              alignItems: "center",
                              mt: 2,
                              color: "text.secondary",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                              <Typography variant="caption">
                                Letti: {avviso.avvisiLetti}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <VisibilityOffIcon fontSize="small" />
                              <Typography variant="caption">
                                Non Letti: {avviso.avvisiNonLetti}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                        <Divider />
                        <CardActions
                          sx={{
                            justifyContent: "flex-end",
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleOpenReadDialog(avviso)}
                          >
                            Apri Avviso
                          </Button>
                        </CardActions>
                      </Card>
                    );
                  })
                )}
                {/* Pagination */}
                {totalPages > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      pt: 3,
                      pb: 1,
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Box>
                )}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      <AddDialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSave={addAvviso}
      />

      <ModifyDialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={(data) => {
          addAvviso(data);
          setOpenModal(false);
        }}
        avviso={selectedAvviso}
      />

      <ReadDialog
        open={openReadModal}
        onClose={() => setOpenReadModal(false)}
        avviso={selectedAvviso}
      />
    </Container>
  );
};

export const AvvisiPage = () => {
  return <AvvisiContent />;
};
